import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { collection, doc, getDoc, getDocs, query, runTransaction, where } from 'firebase/firestore';

import { firestore } from '../firebase';
import {
  DEFAULT_MORNING_NOTIFICATION_MINUTE,
  DEFAULT_REMINDER_MINUTE,
  userSchema,
} from '../../shared/schemas';
import { errorMessage } from '../lib/errors';
import { useAppState } from '../lib/appState';
import {
  browserTimeZone,
  describeTime,
  fromTimeInput,
  landsAfterCutoff,
  toTimeInput,
} from '../lib/reminder';
import {
  PermissionDeniedError,
  isIOSBrowser,
  registerDevice,
  unregisterDevice,
} from '../lib/notifications';
import SignInMethods from './SignInMethods';

type Notice = { text: string; tone: 'good' | 'bad' } | null;

/** The four things that can be notified about, each independent of the others. */
type NotificationKind = 'reminder' | 'morning' | 'crown' | 'friend';

const SETTING_FIELD: Record<NotificationKind, string> = {
  reminder: 'notificationsEnabled',
  morning: 'morningNotificationEnabled',
  crown: 'crownNotificationsEnabled',
  friend: 'friendNotificationsEnabled',
};

/** The two notifications with a chosen time of day, each with its own Firestore field. */
type TimedKind = 'reminder' | 'morning';

const MINUTE_FIELD: Record<TimedKind, string> = {
  reminder: 'reminderMinuteOfDay',
  morning: 'morningNotificationMinuteOfDay',
};

function Settings() {
  const { userId } = useAppState();

  const [displayName, setDisplayName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const [enabled, setEnabled] = useState<Record<NotificationKind, boolean>>({
    reminder: false,
    morning: false,
    crown: false,
    friend: false,
  });
  const [minutes, setMinutes] = useState<Record<TimedKind, number>>({
    reminder: DEFAULT_REMINDER_MINUTE,
    morning: DEFAULT_MORNING_NOTIFICATION_MINUTE,
  });
  const [timeZone, setTimeZone] = useState(browserTimeZone());
  const [busy, setBusy] = useState<NotificationKind | null>(null);
  const [timeBusy, setTimeBusy] = useState<TimedKind | null>(null);

  useEffect(() => {
    getDoc(doc(firestore, 'users', userId))
      .then((snapshot) => {
        if (!snapshot.exists()) return;
        const parsed = userSchema.safeParse({ ...snapshot.data(), id: snapshot.id });
        if (!parsed.success) return;

        const settings = parsed.data.settings;
        setDisplayName(settings.displayName || parsed.data.displayName || '');
        setEnabled({
          reminder: settings.notificationsEnabled,
          morning: settings.morningNotificationEnabled,
          crown: settings.crownNotificationsEnabled,
          friend: settings.friendNotificationsEnabled,
        });
        setMinutes({
          reminder: settings.reminderMinuteOfDay,
          morning: settings.morningNotificationMinuteOfDay,
        });
        if (settings.timeZone) setTimeZone(settings.timeZone);
      })
      .catch((error) => console.error('[Settings] could not load settings:', error));
  }, [userId]);

  const writeSettings = useCallback(
    (fields: Record<string, unknown>) =>
      runTransaction(firestore, async (transaction) => {
        const ref = doc(firestore, 'users', userId);
        if (!(await transaction.get(ref)).exists()) throw new Error('Your profile is missing.');
        transaction.update(ref, { ...fields, 'settings.lastUpdated': new Date().toISOString() });
      }),
    [userId],
  );

  /** Appends a number until the name is free, ignoring this user's own claim. */
  const uniqueName = useCallback(
    async (base: string): Promise<string> => {
      const taken = async (candidate: string) => {
        const snapshot = await getDocs(
          query(collection(firestore, 'users'), where('settings.displayName', '==', candidate)),
        );
        return !snapshot.empty && !(snapshot.size === 1 && snapshot.docs[0].id === userId);
      };

      if (!(await taken(base))) return base;
      for (let n = 1; n < 100; n += 1) {
        if (!(await taken(`${base}${n}`))) return `${base}${n}`;
      }
      throw new Error('Could not find a free variation of that name.');
    },
    [userId],
  );

  const handleSaveName = async () => {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setNotice({ text: 'Your name cannot be empty.', tone: 'bad' });
      return;
    }

    setSavingName(true);
    try {
      const settled = await uniqueName(trimmed);
      await writeSettings({
        'settings.displayName': settled,
        'settings.displayNameLower': settled.toLowerCase(),
        // Kept in step for documents written before settings existed.
        displayName: settled,
      });

      setDisplayName(settled);
      setNotice({
        text: settled === trimmed ? 'Name saved.' : `That one was taken, so you are ${settled} now.`,
        tone: 'good',
      });
    } catch (error) {
      setNotice({ text: `Could not save that: ${errorMessage(error)}`, tone: 'bad' });
    } finally {
      setSavingName(false);
    }
  };

  /**
   * Turning any of the four on registers this device if it is not already.
   * Turning the last one off unregisters it, so a browser that wants nothing
   * stops holding a token.
   */
  const toggle = async (kind: NotificationKind) => {
    const next = !enabled[kind];
    setBusy(kind);
    setNotice(null);

    try {
      // Always re-register when switching something on: permission may already
      // be granted while this particular browser is new to the account, and
      // arrayUnion makes a repeat harmless.
      if (next) await registerDevice(userId);

      await writeSettings({ [`settings.${SETTING_FIELD[kind]}`]: next });

      const updated = { ...enabled, [kind]: next };
      setEnabled(updated);

      const allOff = !updated.reminder && !updated.morning && !updated.crown && !updated.friend;
      if (!next && allOff) {
        await unregisterDevice(userId);
      }
    } catch (error) {
      if (error instanceof PermissionDeniedError) {
        setNotice({
          text: isIOSBrowser()
            ? 'iPhones only allow notifications once the app is on your home screen. There are steps under How it works.'
            : 'Your browser blocked notifications. Allow them in site settings and try again.',
          tone: 'bad',
        });
      } else {
        console.error('[Settings] notification toggle failed:', error);
        setNotice({ text: `Could not change that: ${errorMessage(error)}`, tone: 'bad' });
      }
    } finally {
      setBusy(null);
    }
  };

  const handleTimeChange = async (kind: TimedKind, value: string) => {
    const minute = fromTimeInput(value);
    if (minute === null) return;

    setMinutes((current) => ({ ...current, [kind]: minute }));
    setTimeBusy(kind);

    try {
      const zone = browserTimeZone();
      setTimeZone(zone);
      await writeSettings({
        [`settings.${MINUTE_FIELD[kind]}`]: minute,
        'settings.timeZone': zone,
      });
    } catch (error) {
      setNotice({ text: `Could not save that time: ${errorMessage(error)}`, tone: 'bad' });
    } finally {
      setTimeBusy(null);
    }
  };

  const tooLate = landsAfterCutoff(minutes.reminder, timeZone);

  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-bold">Settings</h1>

      {notice && (
        <p
          className={`mb-5 rounded-xl border-2 border-ink px-4 py-3 text-sm font-medium text-white shadow-pop-sm ${
            notice.tone === 'good' ? 'bg-lagoon' : 'bg-coral'
          }`}
        >
          {notice.text}
        </p>
      )}

      <section className="rounded-2xl border-2 border-ink bg-white px-5 py-5 shadow-pop">
        <label htmlFor="name" className="font-display font-bold">
          Display name
        </label>
        <p className="mb-3 mt-1 text-sm text-ink/55">This is what other players see.</p>

        <div className="flex gap-2">
          <input
            id="name"
            type="text"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            className="min-w-0 flex-1 rounded-xl border-2 border-ink bg-paper px-4 py-2.5 outline-none focus:shadow-pop-sm"
          />
          <button
            onClick={handleSaveName}
            disabled={savingName}
            className="press shrink-0 rounded-xl border-2 border-ink bg-mustard px-5 py-2.5 font-display font-bold shadow-pop-sm disabled:opacity-50"
          >
            {savingName ? 'Saving' : 'Save'}
          </button>
        </div>
      </section>

      <SignInMethods />

      <h2 className="mb-1 mt-8 font-display text-lg font-bold">Notifications</h2>
      <p className="mb-4 text-sm text-ink/55">
        Each of these is independent. Pick any, or none. No hard feelings.
      </p>

      <div className="space-y-3">
        <Toggle
          label="Morning prompt"
          hint="Today's prompt, as soon as it's up. Whether or not you've posted."
          on={enabled.morning}
          busy={busy === 'morning'}
          onChange={() => toggle('morning')}
        />

        {enabled.morning && (
          <TimePicker
            id="morning-time"
            label="Send it at"
            minute={minutes.morning}
            timeZone={timeZone}
            busy={timeBusy === 'morning'}
            onChange={(value) => handleTimeChange('morning', value)}
          />
        )}

        <Toggle
          label="Daily reminder"
          hint="Only if you have not posted yet. We will not nag twice."
          on={enabled.reminder}
          busy={busy === 'reminder'}
          onChange={() => toggle('reminder')}
        />

        {enabled.reminder && (
          <TimePicker
            id="reminder-time"
            label="Remind me at"
            minute={minutes.reminder}
            timeZone={timeZone}
            busy={timeBusy === 'reminder'}
            onChange={(value) => handleTimeChange('reminder', value)}
          >
            {tooLate ? (
              <p className="mt-3 rounded-lg border-2 border-ink bg-coral px-3 py-2 text-sm font-medium text-white">
                {describeTime(minutes.reminder)} where you are is after the prompt rotates, so this
                reminder would arrive too late to use. Pick an earlier time.
              </p>
            ) : (
              <p className="mt-2 text-xs text-ink/50">
                Prompts change at midnight Pacific. Your reminder lands before then.
              </p>
            )}
          </TimePicker>
        )}

        <Toggle
          label="Crowns"
          hint="When someone crowns one of your puns. At most one an hour."
          on={enabled.crown}
          busy={busy === 'crown'}
          onChange={() => toggle('crown')}
        />

        <Toggle
          label="Friend posts"
          hint="When someone you follow posts a pun."
          on={enabled.friend}
          busy={busy === 'friend'}
          onChange={() => toggle('friend')}
        />
      </div>
    </div>
  );
}

/**
 * A switch, not a button that turns red and says Disable when the thing is
 * working. A switch shows state; a red Disable button reads as a warning.
 */
function Toggle({
  label,
  hint,
  on,
  busy,
  onChange,
}: {
  label: string;
  hint: string;
  on: boolean;
  busy: boolean;
  onChange: () => void;
}) {
  return (
    <button
      onClick={onChange}
      disabled={busy}
      role="switch"
      aria-checked={on}
      className={`flex w-full items-center justify-between gap-4 rounded-xl border-2 border-ink bg-white px-5 py-4 text-left shadow-pop-sm ${
        busy ? 'opacity-60' : ''
      }`}
    >
      <span>
        <span className="block font-display font-bold">{label}</span>
        <span className="mt-0.5 block text-sm text-ink/55">{hint}</span>
      </span>

      <span
        aria-hidden="true"
        className={`flex h-7 w-12 shrink-0 items-center rounded-full border-2 border-ink p-0.5 transition-colors ${
          on ? 'bg-lagoon' : 'bg-ink/15'
        }`}
      >
        <span
          className={`h-5 w-5 rounded-full border-2 border-ink bg-white transition-transform ${
            on ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </span>
    </button>
  );
}

/**
 * The time input shared by both daily notifications, always paired with a
 * plain-English readout. The native `<input type="time">` AM/PM indicator
 * renders differently per browser — sometimes a tiny stepper rather than
 * text — and is easy to misread, so the chosen time is always spelled out in
 * words too rather than only when something is wrong with it.
 */
function TimePicker({
  id,
  label,
  minute,
  timeZone,
  busy,
  onChange,
  children,
}: {
  id: string;
  label: string;
  minute: number;
  timeZone: string;
  busy: boolean;
  onChange: (value: string) => void;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-xl border-2 border-dashed border-ink/30 bg-white/60 px-5 py-4">
      <label htmlFor={id} className="block font-display text-sm font-bold">
        {label}
      </label>

      <div className="mt-2 flex items-center gap-3">
        <input
          id={id}
          type="time"
          value={toTimeInput(minute)}
          onChange={(event) => onChange(event.target.value)}
          disabled={busy}
          className="rounded-xl border-2 border-ink bg-paper px-3 py-2 font-display font-bold outline-none focus:shadow-pop-sm"
        />
        <span className="text-sm text-ink/55">{timeZone.replace(/_/g, ' ')}</span>
      </div>

      <p className="mt-2 text-xs text-ink/50">That's {describeTime(minute)}.</p>

      {children}
    </div>
  );
}

export default Settings;
